import type { NavConfig, NavVisibilityCondition } from '@/types/nav-config';

const isAdminOrDeptAdmin: NavVisibilityCondition = {
    type: 'or',
    conditions: [
        { type: 'role', role: 'isAdmin' },
        { type: 'role', role: 'isDepartmentAdmin' },
    ],
};

const isPGA: NavVisibilityCondition = {
    type: 'role',
    role: 'isPGA',
};

export const adminNavConfig: NavConfig = [
    {
        type: 'heading',
        id: 'super-admin',
        labelKey: 'app.navigation.sectionSuperAdmin',
        visible: { type: 'role', role: 'isDefaultOrganization' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.organizations',
        icon: 'fe-briefcase',
        route: 'organizationsManagement',
        visible: { type: 'role', role: 'isDefaultOrganization' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.simulations',
        icon: 'fe-cpu',
        route: 'simulationsManagement',
        visible: { type: 'role', role: 'isDefaultOrganization' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.prompts',
        icon: 'fe-file-text',
        route: 'promptsManagement',
        visible: { type: 'role', role: 'isDefaultOrganization' },
    },
    {
        type: 'group',
        id: 'templates',
        labelKey: 'app.navigation.templates',
        icon: 'fe-layout',
        visible: { type: 'role', role: 'isDefaultOrganization' },
        children: [
            {
                type: 'link',
                labelKey: 'app.navigation.industries',
                icon: 'fe-file-text',
                route: 'industries',
            },
            {
                type: 'link',
                labelKey: 'app.navigation.industryTemplates',
                icon: 'fe-file-text',
                route: 'templates',
            },
        ],
    },
    {
        type: 'link',
        labelKey: 'app.navigation.consentManagement',
        icon: 'fe-file-text',
        route: 'consentManagement',
        visible: { type: 'role', role: 'isDefaultOrganization' },
    },
    {
        type: 'divider',
        visible: { type: 'role', role: 'isDefaultOrganization' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.notifications',
        icon: 'fe-bell',
        route: 'notificationsManagement',
        badge: { source: 'unreadNotifications', variant: 'danger' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.adminDashboard',
        icon: 'fe-home',
        route: 'adminDashboard',
        visible: {
            type: 'and',
            conditions: [
                { type: 'permission', permission: 'access_reports' },
                { type: 'not', condition: isPGA },
            ],
        },
    },
    {
        type: 'group',
        id: 'orgMenu',
        labelKey: 'app.navigation.organization',
        icon: 'fe-briefcase',
        visible: {
            type: 'permission',
            permission: ['manage_org_settings', 'manage_users', 'manage_departments', 'manage_permissions'],
        },
        children: [
            {
                type: 'link',
                labelKey: 'app.navigation.organizationGeneralSettings',
                icon: 'fe-settings',
                route: 'organizationSettings',
                activeRouteNames: ['organizationSettings', 'organizationBranding'],
                visible: { type: 'permission', permission: 'manage_org_settings' },
            },
            {
                type: 'link',
                labelKey: 'app.navigation.users',
                icon: 'fe-users',
                route: 'usersManagement',
                visible: { type: 'permission', permission: 'manage_users' },
            },
            {
                type: 'link',
                labelKey: 'app.navigation.departments',
                icon: 'fe-columns',
                route: 'departmentsManagement',
                visible: { type: 'permission', permission: 'manage_departments' },
            },
            {
                type: 'link',
                labelKey: 'app.navigation.rolePermissionView',
                icon: 'fe-user-check',
                route: 'rolePermissionView',
                visible: { type: 'permission', permission: 'manage_permissions' },
            },
            {
                type: 'link',
                labelKey: 'app.navigation.licensingAndCohorts',
                icon: 'fe-credit-card',
                route: 'licensingAndCohorts',
                visible: { type: 'permission', permission: 'manage_licenses' },
            },
        ],
    },
    {
        type: 'link',
        labelKey: 'app.navigation.tags',
        icon: 'fe-tag',
        route: 'tags',
        visible: { type: 'permission', permission: 'manage_tags' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.courses',
        icon: 'fe-book-open',
        route: 'coursesManagement',
        visible: { type: 'permission', permission: 'manage_courses' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.competencies',
        icon: 'fe-award',
        route: 'competenciesManagement',
        visible: { type: 'permission', permission: 'manage_competencies' },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.userAnalytics',
        icon: 'fe-bar-chart-2',
        route: 'userAnalytics',
        visible: {
            type: 'and',
            conditions: [isAdminOrDeptAdmin, { type: 'role', role: 'isSuperAdmin' }],
        },
    },
    {
        type: 'link',
        labelKey: 'app.navigation.documents',
        icon: 'fe-file',
        route: 'documentManagement',
        visible: { type: 'permission', permission: 'manage_documents' },
    },
    {
        type: 'group',
        id: 'reports',
        labelKey: 'app.navigation.reportsAndAnalytics',
        icon: 'fe-bar-chart-2',
        tourKey: 'reports-and-analytics',
        children: [
            {
                type: 'link',
                labelKey: 'app.navigation.readinessAndSkills',
                icon: 'fe-award',
                route: 'readinessAndSkills',
                tourKey: 'readiness-and-skills',
                visible: {
                    type: 'and',
                    conditions: [
                        { type: 'permission', permission: 'access_reports' },
                        { type: 'not', condition: isPGA },
                    ],
                },
            },
            {
                type: 'link',
                labelKey: 'app.navigation.activityReport',
                icon: 'fe-award',
                route: 'activityReport',
                tourKey: 'activity-report',
                visible: {
                    type: 'and',
                    conditions: [{ type: 'permission', permission: 'access_reports' }],
                },
            },
            {
                type: 'link',
                labelKey: 'app.navigation.aiAssistantUsageReport',
                icon: 'fe-message-circle',
                route: 'aiAssistantUsageReport',
                visible: {
                    type: 'and',
                    conditions: [
                        { type: 'permission', permission: 'access_reports' },
                        { type: 'feature', feature: 'enableAIAssistant' },
                    ],
                },
            },
            {
                type: 'link',
                labelKey: 'app.navigation.licenseReport',
                icon: 'fe-award',
                route: 'licenseReport',
                visible: {
                    type: 'and',
                    conditions: [
                        { type: 'permission', permission: 'manage_licenses' },
                        { type: 'feature', feature: 'hasLicensePool' },
                    ],
                },
            },
        ],
    },
];
